import { Icon } from "@iconify/react";
import { Add, AddCard, AddCardOutlined, Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { get, isEmpty } from "lodash";
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { User } from "../../api/user";
import { CreateEcgInvoiceDto } from "../../dto/CreateEcgInvoiceDto";
import { GENDERS } from "../../enums/gender.enums";
import { useAuth } from "../../hooks/useAuth";
import { useHorse } from "../../hooks/useHorse";
import { useHorses } from "../../hooks/useHorses";
import { useHubstripe } from "../../hooks/useHubstripe";
import { useLocales } from "../../hooks/useLocales";
import { TBilling } from "../../shared/types/billing.type";
import { TBreeds } from "../../shared/types/breed.type";
import { TExam } from "../../shared/types/exam.type";
import { formatDate } from "../../shared/utils/DateUtils";
import { RootState } from "../../store";
import {
  setAddPaymentMethodModal,
  setIsMethodeAdded,
} from "../../store/reducers/modal.reducer";

import palette from "../../theme/palette";
import HorseAgePicker from "../HorseAgeSelector";
import { useTranslation } from "react-i18next";

interface EcgActionCardProps {
  title: string;
  subtitle: string;
  icon: string;
  iconColor?: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const EcgActionCard = forwardRef<HTMLDivElement, EcgActionCardProps>(
  (
    {
      title,
      subtitle,
      icon,
      iconColor = "#5d76cb",
      onClick,
      disabled,
      loading,
    },
    ref,
  ) => {
    const iconBgColor = `${iconColor}20`;

    return (
      <Card
        ref={ref}
        onClick={!disabled && !loading ? onClick : undefined}
        sx={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          bgcolor: "#333",
          position: "relative",
          backgroundImage: "none",
          transition: "all 0.2s ease-in-out",
          border: "1px solid transparent",
          "&:hover": {
            bgcolor: disabled ? "#333" : "#3d3d3d",
            borderColor: disabled ? "transparent" : iconColor,
            transform: disabled ? "none" : "translateY(-2px)",
          },
        }}
      >
        {loading && (
          <Box
            sx={{
              backgroundColor: "background.paper",
              position: "absolute",
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        )}
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: "14px !important",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1.2,
              borderRadius: "10px",
              bgcolor: iconBgColor,
            }}
          >
            <Icon icon={icon} width={24} height={24} color={iconColor} />
          </Box>

          <Box display="flex" flexDirection="column">
            <Typography variant="subtitle2" fontWeight="600" color="white">
              {title}
            </Typography>
            <Typography variant="caption" color="grey.500">
              {subtitle}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  },
);

EcgActionCard.displayName = "EcgActionCard";

type EcgActionsModalProps = {
  handleEcgBtnClick: (key: string) => void;
  ecgDownloading: boolean;
  exam?: TExam;
};

function EcgActionsModal({
  handleEcgBtnClick,
  ecgDownloading,
  exam,
}: EcgActionsModalProps) {
  const restricted = useSelector<RootState>(
    (state) => state.user.restrictedMode,
  ) as boolean;

  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [openRequest, setOpenRequest] = useState(false);

  const sections = [
    {
      groupTitle: t("ecg:analysisHeader"),
      options: [
        {
          key: "REQUEST_ANALYS",
          title: t("ecg:requestAnalysis:linkText"),
          subtitle: t("ecg:requestAnalysis:subtext"),
          icon: "material-symbols:live-help",
          color: "#4caf50",
          enabled: !restricted,
        },
      ],
    },
    {
      groupTitle: t("ecg:vetDash"),
      options: [
        {
          key: "VISUALISE_ECG",
          title: t("seeInDvet"),
          subtitle: t("seeInDvet:subtitle"),
          icon: "lsicon:view-outline",
          color: "#2196f3",
          enabled: true,
        },
        {
          key: "SHARE_ECG",
          title: t("shareVetModale:shareModalTitle"),
          subtitle: t("shareVetModale:subtitle"),
          icon: "ic:baseline-share",
          color: "#ff9800",
          enabled: !restricted,
        },
      ],
    },
    {
      groupTitle: t("button:download"),
      options: [
        {
          key: "DOWNLOAD_ECG",
          title: t("downloadEcg:buttonText"),
          subtitle: t("ecg:download:subtitle"),
          icon: "pepicons-pencil:cloud-down",
          color: "#38BDF8",
          enabled: !restricted,
          loading: ecgDownloading,
        },
      ],
    },
  ] as const;

  type SectionsType = typeof sections;
  type EcgActionKey = SectionsType[number]["options"][number]["key"];

  const handleAction = (key: EcgActionKey) => {
    key !== "REQUEST_ANALYS" ? handleEcgBtnClick(key) : setOpenRequest(true);
  };

  const onSucess = () => {};

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        {t("ecgActions:title")}
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={open}>
          <Box sx={{ ...modalStyle, p: 4 }}>
            <div id="initial_content_wrapper">
              <Box display={"flex"} justifyContent={"space-between"} mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="white">
                    {t("ecgActions:title")}
                  </Typography>
                  <Typography variant="body2" color="grey.500">
                    {t("ecgActions:subtitle")}
                  </Typography>
                </Box>
                <IconButton
                  sx={{ alignSelf: "self-start" }}
                  onClick={() => setOpen(false)}
                >
                  <Close></Close>
                </IconButton>
              </Box>

              <Stack spacing={3}>
                {sections.map((section) => (
                  <Box key={section.groupTitle}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: "grey.600",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      {section.groupTitle}
                    </Typography>

                    <Stack spacing={1}>
                      {section.options.map((opt) => (
                        <EcgActionCard
                          loading={ecgDownloading && opt.key === "DOWNLOAD_ECG"}
                          key={opt.key}
                          title={opt.title}
                          subtitle={opt.subtitle}
                          icon={opt.icon}
                          iconColor={opt.color}
                          disabled={!opt.enabled}
                          onClick={() => handleAction(opt.key)}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </div>
            <RequestModal
              onSucess={onSucess}
              exam={exam}
              onClose={() => setOpenRequest(false)}
              open={openRequest}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

const intraComCountries = [
  "France",
  "Germany",
  "Austria",
  "Belgium",
  "Bulgaria",
  "Cyprus",
  "Croatia",
  "Denmark",
  "Spain",
  "Estonia",
  "Finland",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Poland",
  "Portugal",
  "Czech Republic",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Sweden",
];

function getTVArequested(company: any, dealCountry: string | null): boolean {
  const country = company?.properties?.pays__livraison_ || dealCountry;

  if (country == null) return true;

  const isIntracom = intraComCountries.includes(country);
  const noNumber =
    !company?.properties?.numero_de_tva_intra_communautaire__facturation_;

  if (country === "France") return true;
  if (isIntracom) return noNumber;
  return false;
}

type ChildModalProps = {
  open: boolean;
  onClose: () => void;
  exam?: TExam;
  onSucess: () => void;
};

function RequestModal({ open, onClose, exam, onSucess }: ChildModalProps) {
  const billings = useSelector<RootState>(
    (state) => state.user.user?.user_billing_infos,
  ) as TBilling[];
  const isPaymentMethodeAdded = useSelector<RootState>(
    (state) => state.modals.isPaymentMethodeAdded,
  ) as boolean;
  const stripe_id = get(billings, "[0].stripe_id");
  const {
    getPaymentMethodsFromCustomerId,
    getCustomerFromCustomerId,
    getCompanyFromCustomerId,
  } = useHubstripe();
  const { currentLang } = useLocales();
  const { t } = useTranslation();

  const { auth } = useAuth();

  const { getBreeds } = useHorses(false, auth?.["@id"]);
  const [isAnonyme, setIsAnonyme] = useState(false);

  const [breeds, setBreeds] = useState<TBreeds[]>([]);

  const [comment, setComment] = useState("");
  const [horse_gender, setHorseGender] = useState(
    exam?.training?.horse_trainer?.horse?.gender,
  );

  const [horse_breeed, setHorseBreed] = useState(
    typeof exam?.training?.horse_trainer?.horse?.breed === "string"
      ? exam?.training?.horse_trainer?.horse?.breed
      : exam?.training?.horse_trainer?.horse?.breed?.["@id"],
  );
  const [birthday, setBirthday] = useState(
    exam?.training?.horse_trainer?.horse?.birthday
      ? dayjs(exam?.training?.horse_trainer?.horse?.birthday)
      : null,
  );

  const [paymentsMethod, setPaymentsMethod] = useState<PaymentMethod[]>([]);
  const [customer, setCustomer] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();
  const [isSouthHemisphere, setIsSouthHemisphere] = useState<boolean>(false);
  const [horse_age, setAge] = useState("");
  const [errors, setErrors] = useState({
    gender: false,
    breed: false,
    birthday: false,
    comment: false,
    horse_age: false,
  });
  const [activeTab, setActiveTab] = useState<number>(0);
  const { updateHorseMutation } = useHorse({ enableQuery: false });

  useEffect(() => {
    if (!open) return;
    getBreeds
      .mutateAsync({ inList: true })
      .then(({ data }) => {
        setBreeds(get(data, "hydra:member", []));
      })
      .catch((err) => {
        console.log({ err });
      });
  }, [open]);

  useEffect(() => {
    if (open && stripe_id) {
      fetchCustomer().then((c) => {
        fetchPaymentsMethods(c?.invoice_settings?.default_payment_method);
      });
      fetchCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe_id, open]);

  useEffect(() => {
    if (isPaymentMethodeAdded) {
      fetchPaymentsMethods(customer?.invoice_settings?.default_payment_method);
      dispatch(setIsMethodeAdded(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentMethodeAdded]);

  const fetchPaymentsMethods = async (defaultPaymentMethodId?: string) => {
    try {
      let response =
        await getPaymentMethodsFromCustomerId.mutateAsync(stripe_id);

      let paymentMethods = get(response.data, "paymentMethods.data", []);

      setPaymentsMethod(paymentMethods);

      if (paymentMethods.length > 0) {
        const defaultId =
          defaultPaymentMethodId &&
          paymentMethods.find(
            (m: PaymentMethod) => m.id === defaultPaymentMethodId,
          )
            ? defaultPaymentMethodId
            : paymentMethods[0].id;
        setSelectedMethod(defaultId);
      }
    } catch (error) {}
  };
  const fetchCustomer = async () => {
    try {
      let response = await getCustomerFromCustomerId.mutateAsync(stripe_id);
      const fetchedCustomer = get(response.data, "customer", null);
      setCustomer(fetchedCustomer);
      return fetchedCustomer;
    } catch (error) {}
  };

  const fetchCompany = async () => {
    try {
      let response = await getCompanyFromCustomerId.mutateAsync(stripe_id);
      setCompany(get(response.data, "company", null));
    } catch (error) {}
  };

  const addPaymentMethode = () => {
    dispatch(setAddPaymentMethodModal(true));
  };

  const showTVA = getTVArequested(company, null);

  const priceWithCurrency = () => {
    const prices: Record<string, number> = {
      eur: 65,
      usd: 75,
      gbp: 60,
      aud: 105,
      nzd: 125,
    };

    const currency = customer?.currency?.toLowerCase() || "eur";
    const price = prices[currency] ?? prices.eur;

    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(price);
  };

  const priceWithCurrencyTTC = () => {
    const prices: Record<string, number> = {
      eur: 65,
      usd: 75,
      gbp: 60,
      aud: 105,
      nzd: 125,
    };

    const currency = customer?.currency?.toLowerCase() || "eur";
    const price = (prices[currency] ?? prices.eur) * 1.2;

    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(price);
  };

  const getBreedsByPriority = (breeds: TBreeds[], priorities: string[]) => {
    return breeds.sort((a, b) => {
      const indexA = priorities.indexOf(a.ifce_code);
      const indexB = priorities.indexOf(b.ifce_code);

      // If both are in the priority list, sort by index in that list
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only a is in the list, it comes first
      if (indexA !== -1) return -1;

      // If only b is in the list, it comes first
      if (indexB !== -1) return 1;

      // Otherwise, maintain original order (or sort alphabetically if you want)
      return 0;
    });
  };

  const breeds_in_list = useMemo(() => {
    if (isEmpty(breeds))
      return {
        breeds: [],
        length: 0,
      };

    let inList = breeds.filter((breed) => breed.in_list);

    return {
      breeds: [
        ...getBreedsByPriority(inList, ["PS", "TF", "AR", "AA", "TE"]),
        ...breeds.filter((breed) => !breed.in_list),
      ],
      length: breeds.filter((breed) => breed.in_list).length - 1,
    };
  }, [breeds]);

  const renderPaymentMethods = () => {
    if (getPaymentMethodsFromCustomerId.isLoading) {
      return <Skeleton variant="rounded" height={100} />;
    }

    if (paymentsMethod.length === 0) {
      return (
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          gap={1.5}
        >
          <Button
            onClick={addPaymentMethode}
            variant="outlined"
            startIcon={<AddCardOutlined />}
          >
            {t("eqmSub:incomplete")}
          </Button>
        </Box>
      );
    }

    return (
      <RadioGroup
        value={selectedMethod}
        onChange={(e) => setSelectedMethod(e.target.value)}
      >
        <Stack spacing={1.5}>
          {paymentsMethod.map((method: any) => {
            const isSelected = selectedMethod === method.id;
            const isCard = method.type === "card";
            const isSepa = method.type === "sepa_debit";

            return (
              <Paper
                key={method.id}
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: "pointer",
                  bgcolor: isSelected
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  borderColor: isSelected ? "primary.main" : "#444",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "primary.light" },
                }}
                onClick={() => setSelectedMethod(method.id)}
              >
                <FormControlLabel
                  value={method.id}
                  control={<Radio size="small" />}
                  sx={{ width: "100%", m: 0, alignItems: "flex-start" }}
                  label={
                    <Box display="flex" alignItems="center" gap={2} ml={1}>
                      {/* Dynamic Icon based on type */}
                      <Icon
                        icon={
                          isCard
                            ? "fluent:card-ui-24-filled"
                            : "fluent:building-bank-24-filled"
                        }
                        width={24}
                        style={{ color: isSelected ? "#1976d2" : "#999" }}
                      />
                      <Box>
                        {isCard && (
                          <>
                            <Typography
                              variant="subtitle2"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {method.card.brand} •••• {method.card.last4}
                            </Typography>
                            <Typography variant="caption" color="grey.500">
                              {t("billing:expires")} {method.card.exp_month}/
                              {method.card.exp_year}
                            </Typography>
                          </>
                        )}
                        {isSepa && (
                          <>
                            <Typography variant="subtitle2">
                              {t("billing:sepa")} •••• {method.sepa_debit.last4}
                            </Typography>
                            <Typography variant="caption" color="grey.500">
                              Bank Code:{" "}
                              {method?.sepa_debit?.bank_code || "Direct Debit"}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </Paper>
            );
          })}
        </Stack>
      </RadioGroup>
    );
  };

  const createRecap = useMemo(() => {
    if (!exam) return "";

    return t("ecg:requestAnalysis:recap", {
      horsename: `<b style="color:#9683ec">${exam.training?.horse_trainer?.horse?.name}</b>`,
      date: `<b style="color:#9683ec">${formatDate(exam.training.creation_date, "dddd Do MMM YYYY", currentLang as any)}</b>`,
      time: `<b style="color:#9683ec">${formatDate(exam.training.creation_date, "hh:mm A", currentLang as any)}</b>`,
      interpolation: { escapeValue: false },
    });
  }, [exam, currentLang]);

  const pay = async () => {
    try {
      console.log("[pay] clicked", {
        selectedMethod,
        customer,
        exam,
        stripe_id,
      });

      if (!process.env.REACT_APP_QUOTE_API_BASE_URI) {
        toast("Quote api link not found", { type: "error" });
        return;
      }

      if (!customer) {
        toast(t("error:customernotFound"), { type: "error" });
        return;
      }
      if (!exam) {
        return;
      }
      const isAgeTabActive = activeTab === 0;

      const newErrors = {
        gender: !horse_gender,
        breed: !horse_breeed, // Fixed typo from horse_breeed
        comment: !comment,
        // Only validate birthday if we are NOT on the age tab
        birthday: !isAgeTabActive ? !birthday : false,
        // Only validate horse_age if we ARE on the age tab
        horse_age: isAgeTabActive ? !horse_age : false,
      };

      setErrors(newErrors);

      // If any are true, stop execution
      if (Object.values(newErrors).some(Boolean)) {
        return;
      }
      let breed = breeds_in_list.breeds.find(
        (breed) => breed["@id"] === horse_breeed,
      );
      let gender = GENDERS.find((gender) => gender.value === horse_gender);
      const age = dayjs().diff(dayjs(birthday), "year");

      setIsSaving(true);

      let dto: CreateEcgInvoiceDto = {
        customerId: customer.id,
        paymentMethodId: selectedMethod,
        comment,
        horse_breed: breed?.in_list
          ? t("breed:" + breed.ifce_code)
          : breed?.name,
        horse_gender: t(gender?.key ?? ""),
        equimetre_user_email: auth.email,
        horse_age: isAgeTabActive ? parseInt(horse_age) : age,
        equimetre_user_id: auth.id,
        equimetre_user_full_name: auth.full_name,
        anonymize: isAnonyme,
        training_id: exam?.training.id,
        horse_name: isAnonyme
          ? "XXX"
          : (exam?.training?.horse_trainer?.horse?.display_name ??
            exam?.training?.horse_trainer?.horse?.name ??
            "Undefined"),
      };
      await User.createEcgInvoice(dto);

      let id = exam?.training?.horse_trainer?.horse?.id;

      if (isAgeTabActive && id) {
        let data: any = {
          age_edit: parseInt(horse_age),
          south_hemisphere: isSouthHemisphere,
        };

        await updateHorseMutation.mutateAsync({
          id,
          data,
        });
      }

      toast(t("success:ecgRequest"), { type: "success" });

      onClose();

      onSucess();
    } catch (error: any) {
      toast(t("error:generic"), { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const isCustomerInfosLoading =
    getCustomerFromCustomerId.isLoading ||
    getPaymentMethodsFromCustomerId.isLoading;

  const renderHorseName = useMemo(() => {
    let horseName = exam?.training?.horse_trainer?.horse?.name ?? "";

    return !isAnonyme ? horseName : `XXXX`;
  }, [exam?.id, isAnonyme]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a", // Your dark theme background
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: { xs: "12px 16px", sm: "16px 24px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: { xs: 1.5, sm: 3 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                color="white"
                sx={{
                  fontSize: { xs: "1.05rem", sm: "1.25rem" },
                  lineHeight: { xs: 1.25, sm: 1.6 },
                }}
              >
                {t("ecg:requestAnalysis:linkText")}
              </Typography>

              <Typography
                variant="body2"
                color="grey.500"
                sx={{
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  lineHeight: { xs: 1.25, sm: 1.43 },
                }}
              >
                {t("ecg:requestAnalysis:confirmHint")}
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                alignSelf: "flex-start",
                mt: { xs: -0.25, sm: 0 },
                mr: { xs: -0.5, sm: 0 },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isCustomerInfosLoading && (
            <Box
              sx={{
                inset: 0,
                zIndex: 10,
                position: "fixed",
                backgroundColor: "rgba(0,0,0,.3)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box>
            <Card
              sx={{
                bgcolor: "#333",
              }}
            >
              <CardContent>
                <Typography variant="body2">
                  <span
                    dangerouslySetInnerHTML={{ __html: createRecap }}
                  ></span>
                </Typography>
              </CardContent>
            </Card>
            <Stack direction={"column"} spacing={2} mt={2}>
              <Typography variant="h4">{t("filter:horse")}</Typography>
              <FormControl fullWidth>
                <TextField
                  label={t("addHorse:horseName")}
                  value={renderHorseName}
                  disabled
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isAnonyme}
                      onChange={(_, checked) => setIsAnonyme(checked)}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                    >
                      {t("anonymizeHorseLabel")}
                      <Tooltip
                        arrow
                        placement="right"
                        title={t("anonymizeHorseHint")}
                      >
                        <Icon
                          icon={"eva:info-outline"}
                          fontSize={18}
                          color={palette.dark.info.main}
                        ></Icon>
                      </Tooltip>
                    </Typography>
                  }
                />
              </FormControl>

              <FormControl fullWidth required error={errors.gender}>
                <InputLabel id="demo-simple-select-label">
                  {t("gender")}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  required
                  label={t("gender")}
                  onChange={(e) => {
                    setHorseGender(String(e.target.value));
                  }}
                  defaultValue={horse_gender}
                >
                  {GENDERS.map((gender) => (
                    <MenuItem key={gender.value} value={gender.value}>
                      {t(gender.key)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required error={errors.breed}>
                <InputLabel id="demo-simple-select-label">
                  {t("breed:breed")}
                </InputLabel>

                <Select
                  onChange={(e) => {
                    setHorseBreed(e.target.value);
                  }}
                  defaultValue={horse_breeed}
                  required
                  label={t("breed:breed")}
                >
                  {breeds_in_list.breeds.map((breed) => (
                    <MenuItem key={breed["@id"]} value={breed["@id"]}>
                      {breed.in_list
                        ? t("breed:" + breed.ifce_code)
                        : breed.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <HorseAgePicker
                birthday={birthday}
                setBirthday={setBirthday}
                age={horse_age}
                setAge={setAge}
                is_south_hemisphere={isSouthHemisphere}
                setIsSouthHemisphere={setIsSouthHemisphere}
                errors={errors}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />

              <TextField
                id="outlined-multiline-static"
                label={t("qualification:comment")}
                value={comment}
                required
                onChange={(e) => setComment(e.target.value)}
                fullWidth
                error={errors.comment}
                multiline
                helperText={t("ecg:requestAnalysis:helperText")}
                rows={4}
              />
            </Stack>
            <Stack
              direction={"column"}
              spacing={1}
              sx={{ mt: 4, borderTop: "1px solid #333", pt: 2 }}
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={2}
                flexWrap={"wrap"}
                alignItems={"center"}
                justifyContent={"space-between"}
                mb={2}
              >
                <Typography variant="subtitle1">
                  {t("billing:payment_methods")}
                </Typography>
                {!isEmpty(paymentsMethod) && (
                  <Button
                    onClick={addPaymentMethode}
                    variant="outlined"
                    startIcon={<AddCardOutlined />}
                  >
                    {t("eqmSub:incomplete")}
                  </Button>
                )}
              </Box>
              {renderPaymentMethods()}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid #333",
            display: "flex",
            flexDirection: "column",
            padding: { xs: "12px 16px", sm: "16px 24px" },
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          {/* Row 1: Total Title + Price Breakdown */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: "0.9rem", sm: "1rem" },
                lineHeight: { xs: 1.2, sm: 1.75 },
              }}
            >
              {t("firstSection:total")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                flexShrink: 0,
              }}
            >
              {showTVA ? (
                <>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      lineHeight: { xs: 1.2, sm: 1.6 },
                    }}
                  >
                    {priceWithCurrencyTTC()} incl. VAT
                  </Typography>
                  <Typography
                    variant="caption"
                    color="grey.500"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      lineHeight: { xs: 1.2, sm: 1.66 },
                    }}
                  >
                    {priceWithCurrency()} excl. VAT
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                    lineHeight: { xs: 1.2, sm: 1.6 },
                  }}
                >
                  {priceWithCurrency()} excl. VAT
                </Typography>
              )}
            </Box>
          </Box>

          {/* Row 2: Full Width Explicatif Copy */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            <Typography
              variant="caption"
              color="grey.500"
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                lineHeight: { xs: 1.2, sm: 1.66 },
                display: "block",
              }}
            >
              {t("ecg:requestAnalysis:consent")}
            </Typography>

            <Typography
              variant="caption"
              fontStyle="italic"
              color="grey.500"
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                lineHeight: { xs: 1.2, sm: 1.66 },
                display: "block",
                mt: { xs: 0.5, sm: 2 },
              }}
            >
              {t("ecg:requestAnalysis:paymentHint")}
            </Typography>
          </Box>

          {/* Confirm & Pay Button */}
          <LoadingButton
            onClick={pay}
            disabled={!selectedMethod}
            loading={isSaving}
            fullWidth
            variant="contained"
            sx={{
              mt: { xs: 1.5, sm: 4 },
              py: { xs: 0.75, sm: 1 },
            }}
          >
            {t("button:confirmAndPay")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
// --- Styles ---
const modalStyle = {
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "90%",
    md: 520,
    sm: 520,
  },
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 1,
  bgcolor: "#1e1e1e",
  boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.5)",
  outline: "none",
};

export default EcgActionsModal;
